import { useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { Car, Plus, UserCheck } from 'lucide-react';

import {
  AVISO_DE_CADASTRO_INDISPONIVEL,
  mostrarAviso,
} from '@/modulos/operacao/comum/cadastroIndisponivel';
import { formatarData } from '@/nucleo/formato/datas';
import { EmptyState } from '@/ui/EmptyState';
import { ResponsiveModal } from '@/ui/ResponsiveModal';
import { Spinner } from '@/ui/Spinner';
import { StatusBadge, type StatusVariant } from '@/ui/StatusBadge';
import { Badge } from '@/ui/base/badge';
import { Button } from '@/ui/base/button';
import { Card, CardContent } from '@/ui/base/card';
import { Input } from '@/ui/base/input';
import { Label } from '@/ui/base/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/ui/base/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/ui/base/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/ui/base/tabs';

import {
  useFrotaDoCredenciado,
  useVinculosDoCredenciado,
  type VinculoDoCredenciado,
} from './detalheDados';

interface AbaFrotaEEquipeProps {
  credenciadoId: string;
}

const SELO_DO_VINCULO: Record<VinculoDoCredenciado['status_vinculo'], StatusVariant> = {
  ATIVO: 'ativo',
  PENDENTE: 'pendente',
  INATIVO: 'inativo',
};

/**
 * Aba "Frota & Equipe" — três sub-abas: veículos vinculados, condutores e
 * monitores. Cada sub-aba de pessoa tem o próprio "Novo Vínculo", porque o
 * protótipo já pré-selecionava o tipo pela aba de origem.
 */
export function AbaFrotaEEquipe({ credenciadoId }: AbaFrotaEEquipeProps) {
  const { data: frota } = useFrotaDoCredenciado(credenciadoId);
  const { data: vinculos } = useVinculosDoCredenciado(credenciadoId);

  const condutores = vinculos?.filter((v) => v.tipo_pessoa_vinculada === 'CONDUTOR') ?? [];
  const monitores = vinculos?.filter((v) => v.tipo_pessoa_vinculada === 'MONITOR') ?? [];

  return (
    <Tabs defaultValue="veiculos" className="space-y-4">
      <TabsList>
        <TabsTrigger value="veiculos">
          Veículos
          {frota && <span className="text-muted-foreground ml-1.5 text-xs">({frota.length})</span>}
        </TabsTrigger>
        <TabsTrigger value="condutores">
          Condutores
          {vinculos && (
            <span className="text-muted-foreground ml-1.5 text-xs">({condutores.length})</span>
          )}
        </TabsTrigger>
        <TabsTrigger value="monitores">
          Monitores
          {vinculos && (
            <span className="text-muted-foreground ml-1.5 text-xs">({monitores.length})</span>
          )}
        </TabsTrigger>
      </TabsList>

      <TabsContent value="veiculos">
        <TabelaDeVeiculos credenciadoId={credenciadoId} />
      </TabsContent>
      <TabsContent value="condutores">
        <TabelaDeVinculos credenciadoId={credenciadoId} tipo="CONDUTOR" itens={condutores} />
      </TabsContent>
      <TabsContent value="monitores">
        <TabelaDeVinculos credenciadoId={credenciadoId} tipo="MONITOR" itens={monitores} />
      </TabsContent>
    </Tabs>
  );
}

function TabelaDeVeiculos({ credenciadoId }: { credenciadoId: string }) {
  const navigate = useNavigate();
  const { data: veiculos, isLoading } = useFrotaDoCredenciado(credenciadoId);

  return (
    <Card>
      <CardContent className="space-y-4 p-6">
        {isLoading ? (
          <Spinner size="sm" />
        ) : !veiculos || veiculos.length === 0 ? (
          <EmptyState
            icon={Car}
            title="Nenhum veículo cadastrado"
            description="Os veículos vinculados a este credenciado serão exibidos aqui."
          />
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Placa</TableHead>
                <TableHead>Marca/Modelo</TableHead>
                <TableHead>Ano</TableHead>
                <TableHead>Bancada</TableHead>
                <TableHead>Inscrito</TableHead>
                <TableHead>Status</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {veiculos.map((veiculo) => (
                <TableRow
                  key={veiculo.id}
                  className="hover:bg-muted/50 cursor-pointer"
                  role="button"
                  tabIndex={0}
                  onClick={() => navigate(`/operacao/veiculos/${veiculo.slug}`)}
                  onKeyDown={(evento) => {
                    if (evento.key === 'Enter' || evento.key === ' ') {
                      navigate(`/operacao/veiculos/${veiculo.slug}`);
                    }
                  }}
                >
                  <TableCell className="font-mono font-medium">{veiculo.placa}</TableCell>
                  <TableCell>
                    {[veiculo.marca, veiculo.modelo].filter(Boolean).join(' ') || '—'}
                  </TableCell>
                  <TableCell>{veiculo.ano_fabricacao ?? '—'}</TableCell>
                  <TableCell>{veiculo.tipo_bancada ?? '—'}</TableCell>
                  <TableCell>
                    <Badge variant={veiculo.ja_inscrito ? 'default' : 'outline'}>
                      {veiculo.ja_inscrito ? 'Sim' : 'Não'}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    <StatusBadge
                      status={veiculo.status.toLowerCase() as StatusVariant}
                      label={veiculo.status}
                    />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </CardContent>
    </Card>
  );
}

interface TabelaDeVinculosProps {
  credenciadoId: string;
  tipo: 'CONDUTOR' | 'MONITOR';
  itens: VinculoDoCredenciado[];
}

function TabelaDeVinculos({ credenciadoId, tipo, itens }: TabelaDeVinculosProps) {
  const navigate = useNavigate();
  const [dialogAberto, setDialogAberto] = useState(false);
  const rotuloSingular = tipo === 'CONDUTOR' ? 'condutor' : 'monitor';
  const rota = tipo === 'CONDUTOR' ? '/operacao/condutores' : '/operacao/monitores';

  return (
    <Card>
      <CardContent className="space-y-4 p-6">
        <div className="flex justify-end">
          <Button size="sm" onClick={() => setDialogAberto(true)}>
            <Plus className="mr-1 h-4 w-4" aria-hidden="true" /> Novo{' '}
            {rotuloSingular === 'condutor' ? 'Condutor' : 'Monitor'}
          </Button>
        </div>
        {itens.length === 0 ? (
          <EmptyState
            icon={UserCheck}
            title={`Nenhum ${rotuloSingular} vinculado`}
            description="Vínculos empregatícios serão exibidos aqui quando cadastrados."
          />
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Pessoa</TableHead>
                <TableHead>Vínculo</TableHead>
                <TableHead>Admissão</TableHead>
                <TableHead>Status</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {itens.map((vinculo) => (
                <TableRow
                  key={vinculo.id}
                  className="hover:bg-muted/50 cursor-pointer"
                  role="button"
                  tabIndex={0}
                  onClick={() => navigate(`${rota}/${vinculo.pessoa_slug}`)}
                  onKeyDown={(evento) => {
                    if (evento.key === 'Enter' || evento.key === ' ') {
                      navigate(`${rota}/${vinculo.pessoa_slug}`);
                    }
                  }}
                >
                  <TableCell className="font-medium">{vinculo.pessoa_nome}</TableCell>
                  <TableCell>{vinculo.tipo_vinculo}</TableCell>
                  <TableCell>{formatarData(vinculo.data_admissao)}</TableCell>
                  <TableCell>
                    <StatusBadge
                      status={SELO_DO_VINCULO[vinculo.status_vinculo]}
                      label={vinculo.status_vinculo}
                    />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </CardContent>

      <NovoVinculoDialog
        open={dialogAberto}
        onOpenChange={setDialogAberto}
        tipo={tipo}
        credenciadoId={credenciadoId}
      />
    </Card>
  );
}

function NovoVinculoDialog({
  open,
  onOpenChange,
  tipo,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  tipo: 'CONDUTOR' | 'MONITOR';
  credenciadoId: string;
}) {
  const [pessoa, setPessoa] = useState('');
  const [tipoVinculo, setTipoVinculo] = useState('CLT');
  const [dataAdmissao, setDataAdmissao] = useState('');
  const rotulo = tipo === 'CONDUTOR' ? 'condutor' : 'monitor';

  const enviar = () => {
    mostrarAviso(AVISO_DE_CADASTRO_INDISPONIVEL);
    onOpenChange(false);
    setPessoa('');
    setTipoVinculo('CLT');
    setDataAdmissao('');
  };

  return (
    <ResponsiveModal
      open={open}
      onOpenChange={onOpenChange}
      title={`Novo vínculo — ${rotulo}`}
      description={`Vincula um ${rotulo} já cadastrado a este credenciado.`}
      footer={
        <>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancelar
          </Button>
          <Button onClick={enviar} disabled={!pessoa}>
            Criar
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        <div>
          <Label>Nome do {rotulo}</Label>
          <Input
            className="mt-1.5"
            value={pessoa}
            onChange={(evento) => setPessoa(evento.target.value)}
            placeholder={`Buscar ${rotulo} pelo nome ou CPF…`}
          />
        </div>
        <div>
          <Label>Tipo de vínculo</Label>
          <Select value={tipoVinculo} onValueChange={setTipoVinculo}>
            <SelectTrigger className="mt-1.5">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="CLT">CLT</SelectItem>
              <SelectItem value="AUTONOMO">Autônomo</SelectItem>
              <SelectItem value="MEI">MEI</SelectItem>
              <SelectItem value="SOCIO">Sócio</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label>Data de admissão</Label>
          <Input
            className="mt-1.5"
            type="date"
            value={dataAdmissao}
            onChange={(evento) => setDataAdmissao(evento.target.value)}
          />
        </div>
      </div>
    </ResponsiveModal>
  );
}
